// src/app/services/adminApi.js
import { api } from "./api";

export const adminAssets = {
  list: (params = {}) => api.get("/api/admin/assets", { params }),
  get: (id) => api.get(`/api/admin/assets/${id}`),
  create: (formData) =>
    api.post("/api/admin/assets", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  update: (id, formData) =>
    api.put(`/api/admin/assets/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  remove: (id) => api.delete(`/api/admin/assets/${id}`),
  publicList: () => api.get("/api/admin/public/assets"),
  bulkDelete: (ids) => api.post("/api/admin/assets/bulk-delete", { ids }),
};

export const adminFurniture = {
  list: (params = {}) => api.get("/api/admin/furniture", { params }),
  get: (id) => api.get(`/api/admin/furniture/${id}`),

  create: (formData) =>
    api.post("/api/admin/furniture", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  update: (id, formData) =>
    api.put(`/api/admin/furniture/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),

  remove: (id) => api.delete(`/api/admin/furniture/${id}`),
};

export const adminMaterials = {
  listMaterials: (params = {}) => api.get("/api/admin/materials", { params }),
  getMaterial: (id) => api.get(`/api/admin/materials/${id}`),
  createMaterial: (payload) => api.post("/api/admin/materials", payload),
  updateMaterial: (id, payload) =>
    api.patch(`/api/admin/materials/${id}`, payload),
  deleteMaterial: (id) => api.delete(`/api/admin/materials/${id}`),
};

export const adminOpenings = {
  listOpenings: (params = {}) => api.get("/api/admin/openings", { params }),
  getOpening: (id) => api.get(`/api/admin/openings/${id}`),
  createOpening: (payload) => api.post("/api/admin/openings", payload),
  updateOpening: (id, payload) =>
    api.patch(`/api/admin/openings/${id}`, payload),
  deleteOpening: (id) => api.delete(`/api/admin/openings/${id}`),
  addOpeningMaterial: (id, payload) =>
    api.post(`/api/admin/openings/${id}/materials`, payload),
  removeOpeningMaterial: (id, linkId) =>
    api.delete(`/api/admin/openings/${id}/materials/${linkId}`),
};

export const adminRevit = {
  stats:       ()      => fetchJSON("/admin/revit/stats"),
  tokens:      (search = "") => fetchJSON(`/admin/revit/tokens${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  revokeToken: (id)    => fetchJSON(`/admin/revit/tokens/${id}`, { method: "DELETE" }),
};

export const admin = {
  // Dashboard cards + charts
  metrics: () => api.get("/api/admin/metrics"),
  metricsUserGrowth: ({ months = 6 } = {}) =>
    api.get(`/api/admin/metrics/user-growth?months=${months}`),
  recentActivity: ({ limit = 20 } = {}) =>
    api.get(`/api/admin/activity/recent?limit=${limit}`),
  systemHealth: () => api.get("/api/admin/system-health"),
  projectsMonthly: (params = {}) =>
    api.get("/api/admin/metrics/projects-monthly", { params }),
  // User Management
  users: (params = {}) => api.get("/api/admin/users", { params }),
  setUser: (id, patch) => api.post(`/api/admin/user/${id}`, patch),
  patchUser: (id, body) => api.put(`/api/admin/users/${id}`, body),

  bulkUpdateUsers: (ids, action) =>
    api.post("/api/admin/users/bulk-update", { ids, action }),
  // Project Management
  submissions: (params = {}) => api.get("/api/admin/projects", { params }),
  approveSubmission: (id) => api.post(`/api/admin/projects/${id}/approve`),
  rejectSubmission: (id) => api.post(`/api/admin/projects/${id}/reject`),
};

export async function adminGetManagerPerms(userId) {
  const res = await api.get(`/api/admin/manager/${userId}/perms`);
  console.log(res);
  if (!res) throw new Error(await res.text());
  return res; // { allow:[], deny:[] }
}

export async function adminSaveManagerPerms(userId, { allow, deny }) {
  const res = await api.post(`/api/admin/manager/${userId}/perms`, {
    allow,
    deny,
  });
  if (!res) throw new Error(await res.text());
  return res; // { ok:true, allow, deny }
}

export async function getUserDetail(userId) {
  const r = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/detail`,
    { credentials: "include" }
  );
  if (!r.ok) throw new Error(`Failed to load user detail (${r.status})`);
  return r.json();
}

export async function deleteUser(userId) {
  const r = await fetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}`,
    { method: "DELETE", credentials: "include" }
  );
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    const msg = body?.message || `Failed to delete user (${r.status})`;
    const code = body?.error;
    const err = new Error(msg);
    err.code = code;
    throw err;
  }
  return r.json();
}

export async function getDeletePreflight(ownerId) {
  return fetchJSON(`/admin/users/${ownerId}/delete-preflight`);
}

export async function listOwners() {
  // owners who already own at least 1 org (for quick merge)
  return fetchJSON(`/admin/owners`);
}

export async function listOrgsByOwner(ownerUserId) {
  // optional helper (if you add an endpoint; or embed in /owners response)
  return fetchJSON(`/admin/owners/${ownerUserId}/orgs`); // or build from your existing APIs
}

export async function deleteOwnerWithTransfer(ownerId, payload) {
  return fetchJSON(`/admin/users/${ownerId}/delete-with-transfer`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
// utility used above (you likely already have it)
async function fetchJSON(url, init = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${url}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...init,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const msg = data?.message || `Request failed (${r.status})`;
    const err = new Error(msg);
    err.status = r.status;
    err.code = data?.error;
    throw err;
  }
  return data;
}
